// Sorting the session's tagged quotes into the report's Critical Incidents
// section: self-harm first, then toxic.
//
// The tagger labels each utterance during the live session and emits it on a
// `speech_tagged` event. The section is built from those tags rather than from
// asking the LLM to pick quotes. It takes the list `useTaggedQuotes` returns —
// already the tagger's own words, in the order they were said. The LLM's
// `codedTranscriptData` carries a tag too, but it is the model's retelling, so
// it is never read here: a section that quotes a student to their teacher has
// to be the transcript, verbatim.
//
// Plain negative-climate quotes are not surfaced anywhere: they still cap a
// collaboration score, but a teacher is told about a quote through Critical
// Incidents alone.

export const SELF_HARM_TAG = "WELFARE:SELF_HARM";
export const TOXIC_TAG = "CLIMATE:TOXIC";
export const NEGATIVE_TAG = "CLIMATE:NEGATIVE";

const TAG_DISPLAY = {
  [SELF_HARM_TAG]: "self harm",
  [TOXIC_TAG]: "toxic",
  [NEGATIVE_TAG]: "negative",
};

/** The tag as the teacher reads it, e.g. `WELFARE:SELF_HARM` -> `self harm`. */
export const tagLabel = (tag) => TAG_DISPLAY[tag] || String(tag || "").toLowerCase();

// A quote may carry its tag as a single string or a list, depending on how the
// report was produced; both are read the same way.
const tagsOf = (entry) => {
  const raw = entry?.tag ?? entry?.tags;
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  return raw ? [String(raw)] : [];
};

const hasTag = (entry, tag) => tagsOf(entry).some((t) => t.toUpperCase() === tag);

const tagged = (quotes) => (Array.isArray(quotes) ? quotes : []);

const toQuote = (entry, tag) => ({
  studentName: entry.studentName || entry.student_name || entry.student || "Unknown",
  qNum: entry.qNum ?? entry.q_num ?? entry.questionNumber ?? null,
  quote: entry.quote,
  // Kept under its wire name: the transcript keys highlights on
  // `${quote_timestamp}|${quote}`.
  quote_timestamp: entry.quote_timestamp ?? entry.quoteTimestamp ?? null,
  tag,
});

/**
 * Quotes that need the teacher's attention, self-harm before toxic.
 *
 * Within a tag the transcript's own order is kept, so the bullets read in the
 * order the words were said. Never capped: a teacher must see every flagged
 * utterance, not the first few.
 *
 * @param {Array} quotes The session's tagged quotes, from `useTaggedQuotes`.
 * @returns {Array<{studentName: string, qNum: number|null, quote: string, tag: string}>}
 */
export const criticalIncidents = (quotes) => {
  const entries = tagged(quotes).filter((e) => e?.quote);

  return [
    ...entries.filter((e) => hasTag(e, SELF_HARM_TAG)).map((e) => toQuote(e, SELF_HARM_TAG)),
    ...entries
      .filter((e) => !hasTag(e, SELF_HARM_TAG) && hasTag(e, TOXIC_TAG))
      .map((e) => toQuote(e, TOXIC_TAG)),
  ];
};

/**
 * The quotes reduced to the tags Critical Incidents surfaces, for a surface
 * that must show a teacher nothing else. The teacher-facing transcript uses
 * this; the admin console keeps every tag the tagger emitted.
 *
 * @param {Array} quotes The session's tagged quotes, from `useTaggedQuotes`.
 * @returns {Array} The same entries, `tags` filtered to self harm and toxic;
 *   entries left tagless are dropped.
 */
export const criticalTagsOnly = (quotes) =>
  tagged(quotes)
    .map((q) => ({
      ...q,
      tags: (Array.isArray(q?.tags) ? q.tags : []).filter((t) => {
        const tag = String(t).toUpperCase();
        return tag === SELF_HARM_TAG || tag === TOXIC_TAG;
      }),
    }))
    .filter((q) => q.tags.length > 0);

/**
 * The incidents rolled up for the at-a-glance strip: one `{tag, count}` per
 * tag, in the order the incidents list them — so self harm stays first.
 *
 * @param {Array<{tag: string}>} incidents From `criticalIncidents`.
 * @returns {Array<{tag: string, count: number}>}
 */
export const incidentTagCounts = (incidents) => {
  const counts = [];
  for (const { tag } of incidents || []) {
    const row = counts.find((c) => c.tag === tag);
    if (row) row.count += 1;
    else counts.push({ tag, count: 1 });
  }
  return counts;
};
